import { motion, type MotionValue } from "framer-motion";
import { type ReactNode } from "react";

import { useDragCommitCleanup, useDragType, type DragTier } from "./dragOffset";

interface DragCarouselProps {
  children: ReactNode;
  prev?: ReactNode;
  next?: ReactNode;
  /** Which MotionValue drives this carousel's horizontal position. */
  dragValue: MotionValue<number>;
  /** Which tier activates preview rendering for this carousel. */
  activeType: Exclude<DragTier, "image">;
  /** Extra classes for the outer container (e.g. "flex-1 min-h-0"). */
  className?: string;
}

/**
 * A carousel strip that slides its children and optional prev/next previews
 * in response to a shared MotionValue. Used at two levels:
 *
 * - StandardLayout page-level (wraps AnimatedOutlet): driven by pageDragX
 * - ContentDetailLayout content-level (wraps AnimatedOutlet): driven by contentDragX
 *
 * The image tier is MediaStrip: its slides persist across index changes,
 * which a route outlet cannot offer. Previews here are positioned just
 * outside the visible area and come into view as the strip slides;
 * overflow-x-clip on the outer container clips content beyond the viewport.
 */
export function DragCarousel({
  children,
  prev,
  next,
  dragValue,
  activeType,
  className,
}: DragCarouselProps) {
  const dragType = useDragType();
  const isActive = dragType === activeType;
  useDragCommitCleanup(activeType);

  return (
    <div
      className={`__carousel-${activeType} relative overflow-x-clip ${className ?? ""}`}
    >
      <motion.div
        style={{ x: dragValue }}
        className={`__carousel-${activeType}-strip relative h-full`}
      >
        {/* Previous page/image preview — positioned just to the left */}
        {isActive && prev && (
          <div
            className={`__carousel-${activeType}-prev pointer-events-none absolute top-0 right-full h-full w-full`}
          >
            {prev}
          </div>
        )}

        {children}

        {/* Next page/image preview — positioned just to the right */}
        {isActive && next && (
          <div
            className={`__carousel-${activeType}-next pointer-events-none absolute top-0 left-full h-full w-full`}
          >
            {next}
          </div>
        )}
      </motion.div>
    </div>
  );
}
